// src/services/dashboard-api.service.ts
import type {
  DashboardAbstract,
  WorkloadSummary,
  PendingTaskGroup,
  SummaryCounts,
  DesignStatusResult,
  BGLapseResult,
  OtherReportsResult,
  HabitationStatus,
  PredBriefReport,
  GrantWiseSummary,
  DistrictWiseSummary,
  MandalSummaryRow,
  QCInspectionResult,
  DashboardFilters,
  TerritorialAbstract,
  TerritorialPending,
} from '../types/dashboard.types';

/**
 * Maps the dashboard filter state to the extended query params the aggregation
 * endpoints accept (scheme type -> grant level; everything else 1:1). Undefined
 * values are dropped by buildQuery, so this is safe to spread.
 */
export function dashFilterExtra(f: DashboardFilters): Record<string, any> {
  return {
    grantLevel: f.schemeType,
    stage: f.stage,
    subStage: f.subStage,
    category: f.category,
    workType: f.workType,
    agencyName: f.agencyName,
    contractorId: f.contractorId,
    territoryId: f.territoryId,
    circleId: f.circleId,
    divisionId: f.divisionId,
    subDivisionId: f.subDivisionId,
    constituencyId: f.constituencyId,
    minCost: f.minCost,
    maxCost: f.maxCost,
    fromDate: f.fromDate,
    toDate: f.toDate,
    searchTerm: f.searchTerm,
  };
}

class DashboardApiService {
  private baseUrl: string =
    process.env.REACT_APP_API_URL || 'https://predworks.alliantprojects.com/api/v1';

  private async request<T>(endpoint: string): Promise<T> {
    const token = localStorage.getItem('token');
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) {
      // Session expired/invalid: mirror the app-wide axios layer (api.service.ts) —
      // clear the stale token and bounce to /login, instead of dead-ending the dashboard
      // on a raw error banner. The guarded /dashboard/* routes 401 on an expired JWT.
      if (response.status === 401) {
        localStorage.removeItem('token');
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `API Error: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }

  private buildQuery(params: Record<string, any>): string {
    const filtered = Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== '',
    );
    if (filtered.length === 0) return '';
    return '?' + new URLSearchParams(filtered.map(([k, v]) => [k, String(v)])).toString();
  }

  // ==========================================
  // MAIN DASHBOARD
  // ==========================================

  async getAbstract(
    grant?: string,
    districtId?: number,
    mandalId?: number,
    extra?: Record<string, any>,
  ): Promise<DashboardAbstract> {
    const q = this.buildQuery({ grant, districtId, mandalId, ...extra });
    return this.request(`/dashboard/abstract${q}`);
  }

  async getWorkloadSummary(
    grant?: string,
    districtId?: number,
    mandalId?: number,
  ): Promise<WorkloadSummary> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/workload-summary${q}`);
  }

  // Abstract status with territorial drill-down. `level` is the level of the
  // children to return; `parentId` is the node being drilled into (omit at the
  // territory root).
  async getTerritorialAbstract(
    level: string = 'territory',
    parentId?: number,
    grant?: string,
  ): Promise<TerritorialAbstract> {
    const q = this.buildQuery({ level, parentId, grant });
    return this.request(`/dashboard/territorial-abstract${q}`);
  }

  // Pending tasks to be monitored, with the same territorial drill-down. Each
  // node reports the 9 pending-task tile counts; the work level returns the
  // per-tile work lists for the mandal.
  async getTerritorialPending(
    level: string = 'territory',
    parentId?: number,
    grant?: string,
  ): Promise<TerritorialPending> {
    const q = this.buildQuery({ level, parentId, grant });
    return this.request(`/dashboard/territorial-pending${q}`);
  }

  // State-level abstract status & financial progress dashboard (the executive
  // single-page view). `params` carries grant + the filter bar selections.
  async getStateLevelDashboard(params?: Record<string, any>): Promise<any> {
    const q = this.buildQuery({ ...(params || {}) });
    return this.request(`/dashboard/state-level${q}`);
  }

  // Road Mileage executive dashboard (auth-gated aggregate of the RM abstract).
  async getRoadMileageDashboard(params?: Record<string, any>): Promise<any> {
    const q = this.buildQuery({ ...(params || {}) });
    return this.request(`/dashboard/road-mileage${q}`);
  }

  // Logged-in user's jurisdiction scope (level + resolved name) for locking geo filters.
  async getMyScope(): Promise<{ isState: boolean; level: string; scopeId: number | null; name: string | null }> {
    return this.request(`/dashboard/my-scope`);
  }

  async getSummaryCounts(
    grant?: string,
    districtId?: number,
    mandalId?: number,
  ): Promise<SummaryCounts> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/summary-counts${q}`);
  }

  // ==========================================
  // PENDING TASKS
  // ==========================================

  async getTSToBeAccorded(grant?: string, districtId?: number, mandalId?: number): Promise<PendingTaskGroup[]> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/pending-tasks/ts-to-be-accorded${q}`);
  }

  async getTenderStage(grant?: string, districtId?: number, mandalId?: number): Promise<PendingTaskGroup[]> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/pending-tasks/tender-stage${q}`);
  }

  async getAgreementsPending(grant?: string, districtId?: number, mandalId?: number): Promise<PendingTaskGroup[]> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/pending-tasks/agreements${q}`);
  }

  async getCRApprovalsPending(grant?: string, districtId?: number, mandalId?: number): Promise<PendingTaskGroup[]> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/pending-tasks/cr-approvals${q}`);
  }

  async getFinalPaymentPending(grant?: string, districtId?: number, mandalId?: number): Promise<PendingTaskGroup[]> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/pending-tasks/final-payment${q}`);
  }

  async getPendingUpdation(grant?: string, districtId?: number, mandalId?: number): Promise<PendingTaskGroup[]> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/pending-tasks/pending-updation${q}`);
  }

  async getEOATWorks(grant?: string, districtId?: number, mandalId?: number): Promise<PendingTaskGroup[]> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/pending-tasks/eoat${q}`);
  }

  async getForestClearancePending(grant?: string, districtId?: number, mandalId?: number): Promise<PendingTaskGroup[]> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/pending-tasks/forest-clearance${q}`);
  }

  // Map taskType slug to API method
  async getPendingTasksByType(
    taskType: string,
    grant?: string,
    districtId?: number,
    mandalId?: number,
    extra?: Record<string, any>,
  ): Promise<PendingTaskGroup[]> {
    const known = [
      'ts-to-be-accorded', 'tender-stage', 'agreements', 'cr-approvals',
      'final-payment', 'pending-updation', 'eoat', 'forest-clearance',
    ];
    if (!known.includes(taskType)) throw new Error(`Unknown pending task type: ${taskType}`);
    const q = this.buildQuery({ grant, districtId, mandalId, ...extra });
    return this.request(`/dashboard/pending-tasks/${taskType}${q}`);
  }

  // ==========================================
  // SUMMARIES
  // ==========================================

  async getGrantWiseSummary(districtId?: number, mandalId?: number): Promise<GrantWiseSummary[]> {
    const q = this.buildQuery({ districtId, mandalId });
    return this.request(`/dashboard/grant-wise-summary${q}`);
  }

  async getDistrictWiseSummary(grant?: string): Promise<DistrictWiseSummary[]> {
    const q = this.buildQuery({ grant });
    return this.request(`/dashboard/district-wise-summary${q}`);
  }

  async getMandalWiseSummary(grant?: string, districtId?: number): Promise<MandalSummaryRow[]> {
    const q = this.buildQuery({ grant, districtId });
    return this.request(`/dashboard/mandal-wise-summary${q}`);
  }

  // ==========================================
  // NEW REPORTS
  // ==========================================

  async getPredBriefReport(
    grant?: string,
    districtId?: number,
    mandalId?: number,
    schemeType?: string,
    extra?: Record<string, any>,
  ): Promise<PredBriefReport> {
    const q = this.buildQuery({ grant, districtId, mandalId, schemeType, ...extra });
    return this.request(`/dashboard/pred-brief-report${q}`);
  }

  async getDesignStatus(grant?: string, districtId?: number, mandalId?: number): Promise<DesignStatusResult> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/design-status${q}`);
  }

  async getBGLapseStatus(): Promise<BGLapseResult> {
    return this.request('/dashboard/bg-lapse-status');
  }

  async getOtherReports(grant?: string, districtId?: number, mandalId?: number): Promise<OtherReportsResult> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/other-reports${q}`);
  }

  async getHabitationStatus(districtId?: number, mandalId?: number): Promise<HabitationStatus> {
    const q = this.buildQuery({ districtId, mandalId });
    return this.request(`/dashboard/habitation-status${q}`);
  }

  async getQCSummary(grant?: string, districtId?: number, mandalId?: number): Promise<QCInspectionResult> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    return this.request(`/dashboard/qc-summary${q}`);
  }

  // ==========================================
  // EXPORT
  // ==========================================

  async exportExcel(grant?: string, districtId?: number, mandalId?: number): Promise<void> {
    const q = this.buildQuery({ grant, districtId, mandalId });
    const token = localStorage.getItem('token');
    const response = await fetch(`${this.baseUrl}/dashboard/export/excel${q}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `PRED_Dashboard_${new Date().toISOString().split('T')[0]}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }

  // ==========================================
  // HELPERS
  // ==========================================

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  }

  formatNumber(n: number): string {
    return new Intl.NumberFormat('en-IN').format(n || 0);
  }
}

export const dashboardApi = new DashboardApiService();
